import type { Monaco } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

/**
 * MonacoModelManager - Manages Monaco editor models for multi-tab editing
 *
 * This singleton class efficiently manages Monaco text models:
 * - Creates and caches models by tab ID
 * - Reuses models when switching tabs (avoids recreation)
 * - Properly disposes models when tabs are closed
 * - Preserves view state (cursor, scroll, selections) per model
 */
class MonacoModelManager {
  private monaco: Monaco | null = null;
  private models: Map<string, editor.ITextModel> = new Map();
  private viewStates: Map<string, editor.ICodeEditorViewState | null> = new Map();

  /**
   * Initialize the manager with the Monaco instance
   * Must be called before using other methods
   */
  initialize(monaco: Monaco): void {
    this.monaco = monaco;
  }

  /**
   * Check if the manager has been initialized
   */
  isInitialized(): boolean {
    return this.monaco !== null;
  }

  /**
   * Get or create a model for a tab
   * If the model already exists, returns it (optionally updating content)
   * If not, creates a new model
   */
  getOrCreateModel(
    tabId: string,
    content: string,
    language: string = 'markdown-frontmatter'
  ): editor.ITextModel | null {
    if (!this.monaco) {
      console.warn('MonacoModelManager not initialized');
      return null;
    }

    // Check if model already exists
    if (this.models.has(tabId)) {
      const model = this.models.get(tabId)!;

      // Only update if content is different to avoid cursor jumps
      if (model.getValue() !== content) {
        // Preserve undo stack by using pushEditOperations
        model.pushEditOperations(
          [],
          [
            {
              range: model.getFullModelRange(),
              text: content,
            },
          ],
          () => null
        );
      }

      return model;
    }

    // Create new model with unique URI
    const uri = this.monaco.Uri.parse(`inmemory://tab/${tabId}`);

    // Check if a model with this URI already exists (cleanup stale model)
    const existingModel = this.monaco.editor.getModel(uri);
    if (existingModel) {
      existingModel.dispose();
    }

    // Create the model
    const model = this.monaco.editor.createModel(content, language, uri);
    this.models.set(tabId, model);

    return model;
  }

  /**
   * Update the content of an existing model
   * Preserves undo history
   */
  updateModel(tabId: string, content: string): void {
    const model = this.models.get(tabId);
    if (!model) {
      console.warn(`No model found for tab ${tabId}`);
      return;
    }

    // Only update if content is different
    if (model.getValue() !== content) {
      model.pushEditOperations(
        [],
        [
          {
            range: model.getFullModelRange(),
            text: content,
          },
        ],
        () => null
      );
    }
  }

  /**
   * Get the current content of a model
   */
  getModelContent(tabId: string): string | null {
    const model = this.models.get(tabId);
    return model ? model.getValue() : null;
  }

  /**
   * Save the view state (cursor, scroll, selections) for a tab
   * Should be called before switching tabs
   */
  saveViewState(tabId: string, editor: editor.IStandaloneCodeEditor): void {
    const viewState = editor.saveViewState();
    this.viewStates.set(tabId, viewState);
  }

  /**
   * Restore the view state for a tab
   * Should be called after switching to a tab
   */
  restoreViewState(tabId: string, editor: editor.IStandaloneCodeEditor): void {
    const viewState = this.viewStates.get(tabId);
    if (viewState) {
      editor.restoreViewState(viewState);
    }
  }

  /**
   * Dispose a model when a tab is closed
   */
  disposeModel(tabId: string): void {
    const model = this.models.get(tabId);
    if (model) {
      model.dispose();
      this.models.delete(tabId);
    }
    this.viewStates.delete(tabId);
  }

  /**
   * Check if a model exists for a tab
   */
  hasModel(tabId: string): boolean {
    return this.models.has(tabId);
  }

  /**
   * Get all tab IDs that have models
   */
  getModelTabIds(): string[] {
    return Array.from(this.models.keys());
  }

  /**
   * Dispose all models
   * Should be called when the editor is unmounted
   */
  disposeAll(): void {
    this.models.forEach((model) => {
      model.dispose();
    });
    this.models.clear();
    this.viewStates.clear();
  }

  /**
   * Clean up models that are not in the provided list of active tab IDs
   * Useful for syncing with EditorTabsContext
   */
  cleanupStaleModels(activeTabIds: string[]): void {
    const activeSet = new Set(activeTabIds);
    const staleIds: string[] = [];

    this.models.forEach((_, tabId) => {
      if (!activeSet.has(tabId)) {
        staleIds.push(tabId);
      }
    });

    staleIds.forEach((tabId) => {
      this.disposeModel(tabId);
    });
  }
}

// Export singleton instance
export const modelManager = new MonacoModelManager();

// Also export the class for testing
export { MonacoModelManager };
